import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus, Search, FileText, Send, Copy, Download, RefreshCw, MoreHorizontal, CheckCircle,
  AlertCircle, ChevronRight, X, Trash2, PanelsTopLeft, Files, Palette, Settings2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { logger } from '../utils/logger';
import { API_URL, STATUS_COLORS, STATUS_LABELS } from '../components/booking/bookingUtils';
import { SendLinkDialog } from '../components/booking/SendLinkDialog';
import { BrandingDialog } from '../components/booking/BrandingDialog';
import { downloadBookingPdf } from '../utils/bookingPdf';

export default function BookingFormsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [publishedTemplates, setPublishedTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Create booking wizard state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomerData, setNewCustomerData] = useState({ full_name: '', phone: '', email: '' });
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [closedWonLeads, setClosedWonLeads] = useState([]);
  const [creating, setCreating] = useState(false);
  const [customerSource, setCustomerSource] = useState('existing');

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isSendLinkOpen, setIsSendLinkOpen] = useState(false);
  const [isBrandingOpen, setIsBrandingOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, templatesRes, publishedRes] = await Promise.all([
        fetch(`${API_URL}/api/booking-forms/bookings`, { credentials: 'include' }),
        fetch(`${API_URL}/api/booking-forms/templates`, { credentials: 'include' }),
        fetch(`${API_URL}/api/booking-forms/published-templates`, { credentials: 'include' })
      ]);
      if (bookingsRes.ok) setBookings(await bookingsRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (publishedRes.ok) setPublishedTemplates(await publishedRes.json());
    } catch (error) {
      logger.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async (search = '') => {
    try {
      const response = await fetch(`${API_URL}/api/financial/customers?search=${encodeURIComponent(search)}`, { credentials: 'include' });
      if (response.ok) setCustomers(await response.json());
    } catch (error) {
      logger.error('Error fetching customers:', error);
    }
  };

  const fetchClosedWonLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads?stage=closed_won&limit=100`, { credentials: 'include' });
      if (response.ok) setClosedWonLeads(await response.json());
    } catch (error) {
      logger.error('Error fetching leads:', error);
    }
  };

  const handleCreateBooking = async () => {
    if (!selectedTemplate) return toast.error('Please select a template');
    setCreating(true);
    try {
      const payload = { template_id: selectedTemplate.template_id, office_responses: {} };
      if (customerSource === 'existing' && selectedCustomer) payload.customer_id = selectedCustomer.customer_id;
      else if (customerSource === 'lead' && selectedCustomer) payload.lead_id = selectedCustomer.lead_id;
      else if (customerSource === 'new') {
        if (!newCustomerData.full_name || !newCustomerData.phone) {
          toast.error('Name and phone are required for new customer');
          return;
        }
        payload.new_customer = newCustomerData;
      }
      const response = await fetch(`${API_URL}/api/booking-forms/bookings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error((await response.json()).detail || 'Failed to create booking');
      const booking = await response.json();
      toast.success('Booking created — fill in the office fields');
      setIsCreateOpen(false);
      resetCreateWizard();
      navigate(`/booking-forms/bookings/${booking.booking_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/booking-forms/bookings/${bookingId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ status })
      });
      if (!response.ok) throw new Error('Failed to update status');
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRequestResubmission = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/booking-forms/bookings/${bookingId}/request-resubmission`, { method: 'POST', credentials: 'include' });
      if (!response.ok) throw new Error('Failed to request resubmission');
      toast.success('Resubmission requested - client can now edit and resubmit');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDuplicateTemplate = async (template) => {
    setDuplicating(template.template_id);
    try {
      const response = await fetch(`${API_URL}/api/booking-forms/templates/${template.template_id}/duplicate`, { method: 'POST', credentials: 'include' });
      if (!response.ok) throw new Error((await response.json()).detail || 'Failed to duplicate template');
      const copy = await response.json();
      toast.success(`Created "${copy.name}" — opening in the builder`);
      navigate(`/booking-forms/templates/${copy.template_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDuplicating(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const endpoint = deleteTarget.type === 'booking'
        ? `${API_URL}/api/booking-forms/bookings/${deleteTarget.id}`
        : `${API_URL}/api/booking-forms/templates/${deleteTarget.id}`;
      const response = await fetch(endpoint, { method: 'DELETE', credentials: 'include' });
      if (!response.ok) throw new Error((await response.json()).detail || `Failed to delete ${deleteTarget.type}`);
      toast.success(`${deleteTarget.type === 'booking' ? 'Booking' : 'Template'} deleted successfully`);
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (type, id, name) => {
    setDeleteTarget({ type, id, name });
    setDeleteConfirmOpen(true);
  };

  const resetCreateWizard = () => {
    setCreateStep(1);
    setSelectedTemplate(null);
    setSelectedCustomer(null);
    setNewCustomerData({ full_name: '', phone: '', email: '' });
    setCustomerSource('existing');
    setCustomerSearchQuery('');
  };

  const filteredBookings = useMemo(() => bookings.filter(b => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || b.customer?.full_name?.toLowerCase().includes(q) || b.customer?.phone?.includes(searchQuery) || b.template_name?.toLowerCase().includes(q);
    return matchesSearch && (statusFilter === 'all' || b.status === statusFilter);
  }), [bookings, searchQuery, statusFilter]);

  const openCreateWizard = () => {
    resetCreateWizard();
    fetchCustomers();
    fetchClosedWonLeads();
    setIsCreateOpen(true);
  };

  const renderCustomerInput = useMemo(() => {
    const q = customerSearchQuery.toLowerCase();
    const filteredCustomers = customers.filter(c => !customerSearchQuery || c.full_name?.toLowerCase().includes(q) || c.phone?.includes(customerSearchQuery)).slice(0, 10);
    const filteredLeads = closedWonLeads.filter(l => !customerSearchQuery || `${l.first_name} ${l.last_name}`.toLowerCase().includes(q) || l.phone?.includes(customerSearchQuery)).slice(0, 10);

    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          {[['existing', 'Existing Customer'], ['lead', 'From Lead'], ['new', 'New Customer']].map(([key, label]) => (
            <Button key={key} type="button" variant={customerSource === key ? 'default' : 'outline'} size="sm"
              onClick={() => { setCustomerSource(key); setSelectedCustomer(null); }} data-testid={`customer-source-${key}`}>
              {label}
            </Button>
          ))}
        </div>

        {customerSource === 'new' ? (
          <div className="space-y-3">
            <div><Label>Full Name *</Label><Input value={newCustomerData.full_name} onChange={(e) => setNewCustomerData(prev => ({ ...prev, full_name: e.target.value }))} placeholder="Enter full name" data-testid="new-customer-name" /></div>
            <div><Label>Phone *</Label><Input value={newCustomerData.phone} onChange={(e) => setNewCustomerData(prev => ({ ...prev, phone: e.target.value }))} placeholder="Enter phone number" data-testid="new-customer-phone" /></div>
            <div><Label>Email</Label><Input type="email" value={newCustomerData.email} onChange={(e) => setNewCustomerData(prev => ({ ...prev, email: e.target.value }))} placeholder="Enter email (optional)" data-testid="new-customer-email" /></div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={customerSearchQuery} className="pl-10" data-testid="customer-search-input"
                onChange={(e) => { setCustomerSearchQuery(e.target.value); if (customerSource === 'existing') fetchCustomers(e.target.value); }}
                placeholder={customerSource === 'existing' ? 'Search customers...' : 'Search closed won leads...'} />
            </div>
            {selectedCustomer ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between" data-testid="selected-customer">
                <div>
                  <p className="font-medium text-green-800">{customerSource === 'lead' ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}` : selectedCustomer.full_name}</p>
                  <p className="text-sm text-green-600">{selectedCustomer.phone}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedCustomer(null)}><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                {(customerSource === 'existing' ? filteredCustomers : filteredLeads).length === 0 ? (
                  <div className="p-4 text-center text-slate-500">{customerSearchQuery ? 'No results found' : `Type to search ${customerSource === 'existing' ? 'customers' : 'closed won leads'}`}</div>
                ) : customerSource === 'existing' ? filteredCustomers.map(c => (
                  <div key={c.customer_id} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedCustomer(c)} data-testid={`customer-option-${c.customer_id}`}>
                    <p className="font-medium">{c.full_name}</p>
                    <p className="text-sm text-slate-500">{c.phone} {c.email && `• ${c.email}`}</p>
                  </div>
                )) : filteredLeads.map(l => (
                  <div key={l.lead_id} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedCustomer(l)} data-testid={`lead-option-${l.lead_id}`}>
                    <p className="font-medium">{l.first_name} {l.last_name}</p>
                    <p className="text-sm text-slate-500">{l.phone} {l.email && `• ${l.email}`}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [customerSource, customerSearchQuery, customers, closedWonLeads, selectedCustomer, newCustomerData]);

  if (loading) {
    return <div className="p-6 flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div></div>;
  }

  const canProceed = createStep === 1
    ? (customerSource === 'new' ? (newCustomerData.full_name && newCustomerData.phone) : !!selectedCustomer)
    : !!selectedTemplate;

  return (
    <div className="p-6 space-y-6" data-testid="booking-forms-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Booking Forms</h1>
          <p className="text-slate-500">Manage booking templates and client bookings</p>
        </div>
        <Button onClick={openCreateWizard} className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="create-booking-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Booking
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="bookings" data-testid="bookings-tab">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="templates" data-testid="templates-tab">Templates ({templates.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="mt-4">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search by customer or template..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="bookings-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="bookings-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardContent className="p-0">
              {filteredBookings.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No bookings found</p>
                  <Button variant="link" onClick={openCreateWizard} className="text-gold mt-2">Create your first booking</Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings.map((booking) => (
                      <TableRow key={booking.booking_id} className="cursor-pointer" onClick={() => navigate(`/booking-forms/bookings/${booking.booking_id}`)} data-testid={`booking-row-${booking.booking_id}`}>
                        <TableCell>
                          <p className="font-medium">{booking.customer?.full_name || 'Unknown'}</p>
                          <p className="text-sm text-slate-500">{booking.customer?.phone}</p>
                        </TableCell>
                        <TableCell>{booking.template_name}</TableCell>
                        <TableCell><Badge className={STATUS_COLORS[booking.status]}>{STATUS_LABELS[booking.status]}</Badge></TableCell>
                        <TableCell className="text-sm text-slate-500">{booking.created_at && format(new Date(booking.created_at), 'MMM d, yyyy')}</TableCell>
                        <TableCell className="text-sm text-slate-500">{booking.submitted_at ? format(new Date(booking.submitted_at), 'MMM d, yyyy h:mm a') : '-'}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`booking-menu-${booking.booking_id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/booking-forms/bookings/${booking.booking_id}`)} data-testid="open-form-action">
                                <PanelsTopLeft className="w-4 h-4 mr-2" /> Open Form
                              </DropdownMenuItem>
                              {booking.status === 'draft' && (
                                <DropdownMenuItem onClick={() => { setSelectedBooking(booking); setIsSendLinkOpen(true); }}>
                                  <Send className="w-4 h-4 mr-2" /> Send Link
                                </DropdownMenuItem>
                              )}
                              {booking.public_url && (
                                <>
                                  <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(booking.public_url); toast.success('Link copied'); }}>
                                    <Copy className="w-4 h-4 mr-2" /> Copy Link
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => { setSelectedBooking(booking); setIsSendLinkOpen(true); }}>
                                    <RefreshCw className="w-4 h-4 mr-2" /> Resend Link
                                  </DropdownMenuItem>
                                </>
                              )}
                              {booking.status === 'submitted' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleUpdateStatus(booking.booking_id, 'verified')}><CheckCircle className="w-4 h-4 mr-2" /> Mark Verified</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleRequestResubmission(booking.booking_id)}><RefreshCw className="w-4 h-4 mr-2" /> Request Resubmission</DropdownMenuItem>
                                </>
                              )}
                              {booking.status === 'verified' && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(booking.booking_id, 'confirmed')}><CheckCircle className="w-4 h-4 mr-2" /> Confirm Booking</DropdownMenuItem>
                              )}
                              {['submitted', 'verified', 'confirmed'].includes(booking.status) && (
                                <DropdownMenuItem onClick={() => downloadBookingPdf(booking.booking_id)}><Download className="w-4 h-4 mr-2" /> Download PDF</DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => confirmDelete('booking', booking.booking_id, booking.customer?.full_name || 'this booking')} className="text-red-600 focus:text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete Booking
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle>Booking Form Templates</CardTitle>
                <CardDescription>Create and manage reusable booking form templates</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsBrandingOpen(true)} data-testid="branding-dialog-trigger">
                  <Palette className="w-4 h-4 mr-2" /> Branding
                </Button>
                <Button onClick={() => navigate('/booking-forms/templates/new')} className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="create-template-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create Template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p>No templates created yet</p>
                  <Button variant="link" onClick={() => navigate('/booking-forms/templates/new')} className="text-gold mt-2">Create your first template</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map(template => (
                    <div key={template.template_id} className="p-4 border rounded-lg hover:border-gold/50 transition-colors" data-testid={`template-card-${template.template_id}`}>
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 cursor-pointer min-w-0" onClick={() => navigate(`/booking-forms/templates/${template.template_id}`)}>
                          <h3 className="font-medium truncate">{template.name}</h3>
                          <p className="text-sm text-slate-500 truncate">{template.description}</p>
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {template.tags?.map(tag => (
                              <Badge key={tag.tag_id} variant="outline" style={{ borderColor: tag.color, color: tag.color }}>{tag.name}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right">
                            <Badge className={template.status === 'published' ? 'bg-green-100 text-green-700' : template.status === 'archived' ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-700'}>
                              {template.status}
                            </Badge>
                            <p className="text-xs text-slate-500 mt-1">v{template.version_count || 0} versions</p>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" title="Edit" onClick={() => navigate(`/booking-forms/templates/${template.template_id}`)} data-testid={`edit-template-${template.template_id}`}>
                              <Settings2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" title="Duplicate" disabled={duplicating === template.template_id}
                              onClick={() => handleDuplicateTemplate(template)} data-testid={`duplicate-template-${template.template_id}`}>
                              <Files className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" title="Delete" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => confirmDelete('template', template.template_id, template.name)} data-testid={`delete-template-${template.template_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Booking Wizard */}
      <Dialog open={isCreateOpen} onOpenChange={(open) => { if (!open) resetCreateWizard(); setIsCreateOpen(open); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="create-booking-dialog">
          <DialogHeader>
            <DialogTitle>Create Booking</DialogTitle>
            <DialogDescription>Step {createStep} of 2: {createStep === 1 ? 'Select Customer' : 'Select Template'}</DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {createStep === 1 && (
              <div className="space-y-4">
                <Label className="text-base font-medium">Customer Information</Label>
                {renderCustomerInput}
              </div>
            )}
            {createStep === 2 && (
              <div className="space-y-4">
                <Label className="text-base font-medium">Select Template</Label>
                {publishedTemplates.length === 0 ? (
                  <Alert><AlertCircle className="w-4 h-4" /><AlertDescription>No published templates available. Please create and publish a template first.</AlertDescription></Alert>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {publishedTemplates.map(template => (
                      <div key={template.template_id} onClick={() => setSelectedTemplate(template)} data-testid={`select-template-${template.template_id}`}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedTemplate?.template_id === template.template_id ? 'border-gold bg-gold/5' : 'hover:border-slate-300'}`}>
                        <p className="font-medium">{template.name}</p>
                        {template.description && <p className="text-sm text-slate-500">{template.description}</p>}
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {template.tags?.map(tag => <Badge key={tag.tag_id} variant="outline" className="text-xs" style={{ borderColor: tag.color, color: tag.color }}>{tag.name}</Badge>)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500">After creating, you'll land on the full office form to fill in office fields and send the client link.</p>
              </div>
            )}
          </div>

          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={() => createStep > 1 ? setCreateStep(createStep - 1) : setIsCreateOpen(false)} data-testid="wizard-back-btn">
              {createStep > 1 ? 'Back' : 'Cancel'}
            </Button>
            {createStep < 2 ? (
              <Button onClick={() => setCreateStep(2)} disabled={!canProceed} className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="wizard-next-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleCreateBooking} disabled={creating || !canProceed} className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="wizard-create-btn">
                {creating ? 'Creating...' : 'Create & Open Form'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <SendLinkDialog open={isSendLinkOpen} onOpenChange={setIsSendLinkOpen} booking={selectedBooking} onSent={fetchData} />
      <BrandingDialog open={isBrandingOpen} onOpenChange={setIsBrandingOpen} />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteTarget?.type === 'booking' ? 'the booking for' : 'the template'} "{deleteTarget?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
